/**
 * Production Route Configuration
 *
 * All protected and public production routes organized by feature area.
 * Routes include authentication requirements, subscription gates, and error boundaries.
 *
 * Route Categories:
 * - Authentication Routes
 * - Protected Report Routes
 * - Dashboard Routes
 * - Assessment Routes
 * - Settings Routes
 * - Portal Routes (Underwriter/Broker)
 * - SC Specific Routes
 * - Common Routes
 * - ML/Glass Dashboard Routes
 * - Placeholder Routes
 * - Legal/Support Routes
 * - Marketing Routes
 * - Default Routes
 */

import React, { Suspense } from 'react';
import { Route, Navigate, useLocation } from 'react-router-dom';
import RequireAuth from '../components/RequireAuth';
import ErrorBoundary from '../components/ErrorBoundary';
import DarkSuspenseFallback from '../components/DarkSuspenseFallback';
import { isDashboardEnabled } from '../config/featureFlags';
import * as LazyComponents from './lazyComponents';
import { DashboardSelector } from './helpers';

// Import non-lazy pages
import Login from '../pages/Login';
import GetStarted from '../pages/GetStarted';
import ContractorLogin from '../pages/ContractorLogin';
// WorkerOnboard/SCOnboard removed - both were unwired duplicates of the real
// flows (/worker-signup and /signup); their routes now redirect (see below)
// Worker onboarding wizard lives at /onboarding (steps 1-2; mobile finishes 3-4)
import ComplianceReportsPage from '../pages/Reports';
import ReportsRouter from '../pages/ReportsRouter';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import Features from '../pages/Features';
import Pricing from '../pages/Pricing';
import LandingPage from '../pages/LandingPage';
import SolutionsPage from '../pages/SolutionsPage';
import NotFound from '../pages/NotFound';
import DomainAwareHome from '../components/DomainAwareHome';
import DebugPage from '../pages/DebugPage';
import DevBoltVerify from '../pages/DevBoltVerify';

/**
 * RedirectWithQuery - query-preserving redirect for legacy onboarding URLs.
 *
 * A plain <Navigate to="/x" /> drops the query string, which would break deep
 * links like /worker-onboard?token=… — carry the query across and let
 * `defaults` add params the target needs (e.g. role=sc for the old SC
 * onboarding URL) without overriding anything the link already carries.
 */
function RedirectWithQuery({ to, defaults }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  Object.entries(defaults || {}).forEach(([key, value]) => {
    if (!params.has(key)) params.set(key, value);
  });
  const query = params.toString();
  return <Navigate to={query ? `${to}?${query}` : to} replace />;
}

/**
 * RedirectWithSection - legacy settings URLs that should land on the canonical hub.
 */
function RedirectWithSection({ section }) {
  return <Navigate to={`/settings?section=${section}`} replace />;
}

/**
 * getProductionRoutes - Returns array of all production route elements
 *
 * @returns {Array<React.ReactElement>} - Array of Route elements for production
 */
export function getProductionRoutes() {
  const routes = [
    ...getAuthRoutes(),
    ...getProtectedReportRoutes(),
    ...getDashboardRoutes(),
    ...getAssessmentRoutes(),
    ...getSettingsRoutes(),
    ...getPortalRoutes(),
    ...getSCRoutes(),
    ...getCommonRoutes(),
    ...getMLRoutes(),
    ...getPlaceholderRoutes(),
    ...getLegalRoutes(),
    ...getMarketingRoutes(),
    ...getDefaultRoutes(),
  ];
  // Dev-only: Bolt component verification (no auth)
  if (import.meta.env.DEV) {
    routes.push(
      <Route key="dev-bolt-verify" path="/dev/bolt-verify" element={<DevBoltVerify />} />
    );
  }
  return routes;
}

// ============================================================================
// AUTHENTICATION ROUTES
// ============================================================================

/**
 * getAuthRoutes - Public authentication and registration routes
 * No authentication required for these routes
 */
function getAuthRoutes() {
  return [
    <Route key="auth-login" path="/login" element={<Login />} />,
    <Route key="auth-signup" path="/signup" element={<GetStarted />} />,
    <Route key="auth-contractor-login" path="/contractor-login" element={<ContractorLogin />} />,
    // Legacy register routes redirect to unified signup
    <Route key="auth-register-gc" path="/register-gc" element={<RedirectWithQuery to="/signup" defaults={{ role: 'gc' }} />} />,
    <Route key="auth-register-sc" path="/register-sc" element={<RedirectWithQuery to="/signup" defaults={{ role: 'sc' }} />} />,
    // Legacy onboarding URLs — superseded by the real flows. /worker-onboard
    // duplicated /worker-signup (which validates short codes via the
    // worker-invites API) and /sc-onboard was an unwired mock of /signup.
    // Query params (?token=, ?company=) survive the redirect.
    <Route key="auth-worker-onboard" path="/worker-onboard" element={<RedirectWithQuery to="/worker-signup" />} />,
    <Route key="auth-sc-onboard" path="/sc-onboard" element={<RedirectWithQuery to="/signup" defaults={{ role: 'sc' }} />} />,
    // Worker signup via invite link - public route with token validation
    <Route
      key="auth-worker-signup"
      path="/worker-signup"
      element={
        <ErrorBoundary>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.WorkerSignup />
          </Suspense>
        </ErrorBoundary>
      }
    />,
    // Team invite - dashboard user signup via invite link
    <Route
      key="auth-team-invite"
      path="/team-invite"
      element={
        <ErrorBoundary>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.TeamInvite />
          </Suspense>
        </ErrorBoundary>
      }
    />,
    // Password reset flow
    <Route
      key="auth-reset-password"
      path="/reset-password"
      element={
        <ErrorBoundary>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.ResetPassword />
          </Suspense>
        </ErrorBoundary>
      }
    />,
    <Route key="onboarding-wizard-alias" path="/onboarding-wizard" element={<Navigate to="/onboarding" replace />} />,
    <Route key="contractor-onboarding-redirect" path="/contractor-onboarding" element={<Navigate to="/command-center" replace />} />,
  ];
}

// ============================================================================
// PROTECTED REPORT ROUTES
// ============================================================================

/**
 * getProtectedReportRoutes - Reports Hub and Compliance Reports
 * Requires authentication and subscription
 */
function getProtectedReportRoutes() {
  return [
    // Reports Hub Route - Using DarkReports for unified dark mode UI
    <Route
      key="reports"
      path="/reports"
      element={
        <RequireAuth
          role={[
            'gcAdmin',
            'scAdmin',
            'admin',
            'worker',
          ]}
        >
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkReports />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,

    // Legacy Compliance Reports Route - kept for backward compatibility
    <Route
      key="compliance-reports"
      path="/compliance-reports"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin']}>
            <ComplianceReportsPage />
        </RequireAuth>
      }
    />,

    // Employee Compliance Reports Route - protected
    <Route
      key="employee-compliance-reports"
      path="/employee-compliance-reports"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin']}>
            <Suspense fallback={<DarkSuspenseFallback />}>
              <LazyComponents.EmployeeComplianceReports />
            </Suspense>
        </RequireAuth>
      }
    />,
  ];
}

// ============================================================================
// DASHBOARD ROUTES
// ============================================================================

/**
 * getDashboardRoutes - Unified Dashboard for all user types
 * Single entry point: /dashboard/:dashboardType? supports construction, subcontractor, industrial, executive, safety_manager, field_supervisor
 * Legacy routes redirect here for backward compatibility
 */
function getDashboardRoutes() {
  return [
    // ========================================================================
    // ACTIVE DASHBOARDS - Command Center Only
    // ========================================================================
    
    /**
     * Command Center Routes - Production Mode
     *
     * Production route: /command-center (requires authentication)
     */
    
    // Command Center - Production (Unified Dashboard for ALL Contractors)
    // This is the ONLY production dashboard for contractors
    // Uses DarkCommandCenter - the new Apple/Tesla-inspired UI
    // All contractor roles (gc, sc, admin, worker) can access this dashboard
    // Include both normalized roles (gc, sc) and backend roles (contractor, subcontractor)
    <Route
      key="command-center"
      path="/command-center"
      element={
        <RequireAuth
          role={[
            'gcAdmin',
            'contractor',
            'scAdmin',
            'subcontractor',
            'admin',
            'worker',
            'foreman',
            'safety_supervisor',
          ]}
        >
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.CommandCenterRoute />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,

    <Route
      key="worker-onboarding"
      path="/onboarding"
      element={
        <RequireAuth
          role={['worker', 'foreman']}
        >
          <ErrorBoundary>
            <Suspense fallback={<DarkSuspenseFallback />}>
              <LazyComponents.WorkerOnboardingWizard />
            </Suspense>
          </ErrorBoundary>
        </RequireAuth>
      }
    />,

    // AI Intelligence Center - Production
    <Route
      key="ai-intelligence"
      path="/ai-intelligence"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.AIIntelligencePage />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,

    // ========================================================================
    // LEGACY REDIRECTS - Redirect all old dashboard routes to Command Center
    // ========================================================================
    
    <Route key="gc-redirect" path="/gc" element={<Navigate to="/command-center" replace />} />,
    <Route key="gc-dashboard-redirect" path="/gc-dashboard" element={<Navigate to="/command-center" replace />} />,
    <Route key="dashboard-redirect" path="/dashboard" element={<Navigate to="/command-center" replace />} />,
    <Route key="dashboard-construction-redirect" path="/dashboard/construction" element={<Navigate to="/command-center" replace />} />,
    <Route key="gc-dashboard-integrated-redirect" path="/gc-dashboard-integrated" element={<Navigate to="/command-center" replace />} />,

    // ========================================================================
    // DEPRECATED DASHBOARDS - These routes now show deprecation messages
    // ========================================================================
    
    // All other dashboard types have been deprecated in favor of Command Center
    <Route key="sc-redirect" path="/sc" element={<Navigate to="/command-center" replace />} />,
    <Route key="sc-dashboard-redirect" path="/sc-dashboard" element={<Navigate to="/command-center" replace />} />,
    <Route key="dashboard-subcontractor-redirect" path="/dashboard/subcontractor" element={<Navigate to="/command-center" replace />} />,
    <Route key="sc-dashboard-integrated-redirect" path="/sc-dashboard-integrated" element={<Navigate to="/command-center" replace />} />,
    
    <Route key="admin-redirect" path="/admin" element={<Navigate to="/command-center" replace />} />,
    <Route key="admin-dashboard-redirect" path="/admin-dashboard" element={<Navigate to="/command-center" replace />} />,
    <Route key="dashboard-executive-redirect" path="/dashboard/executive" element={<Navigate to="/command-center" replace />} />,
    

    // Catch-all for any other dashboard types
    <Route key="dashboard-catchall-redirect" path="/dashboard/:dashboardType" element={<Navigate to="/command-center" replace />} />,
    
    // ========================================================================
    // REMOVED FEATURES
    // ========================================================================
    
    // Unified Dashboard - REMOVED (replaced by Command Center)
    // Widget Marketplace - REMOVED (OSHA widget system deprecated)
    // Industrial Dashboard - REMOVED (not core to product)
    // Broker/Underwriter Dashboards - REMOVED (insurance/Risk Terminal UI archived per RISK-INSURANCE-ARCHIVE)
  ];
}

// ============================================================================
// ASSESSMENT ROUTES
// ============================================================================

/**
 * getAssessmentRoutes - Safety assessments and hazard reporting
 */
function getAssessmentRoutes() {
  return [
    <Route
      key="safety-assessment"
      path="/safety-assessment"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.SafetyAssessment />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="hazard-report"
      path="/hazard-report"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.HazardReport />
          </Suspense>
        </RequireAuth>
      }
    />,
  ];
}

// ============================================================================
// SETTINGS ROUTES
// ============================================================================

/**
 * getSettingsRoutes - Billing and settings pages
 */
function getSettingsRoutes() {
  return [
    <Route
      key="settings-hub"
      path="/settings"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.SettingsHub />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="billing"
      path="/billing"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.Billing />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="gc-settings"
      path="/gc-settings"
      element={<RedirectWithSection section="organization" />}
    />,
    <Route
      key="automation-settings"
      path="/automation-settings"
      element={<Navigate to="/settings/automation" replace />}
    />,
    <Route
      key="settings-automation"
      path="/settings/automation"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin']}>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.AutomationSettings />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="notification-preferences"
      path="/settings/notifications"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.NotificationPreferencesPage />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="integrations-overview"
      path="/settings/integrations"
      element={
        <RequireAuth role={['gcAdmin', 'admin']}>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.IntegrationsOverviewPage />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="procore-integration"
      path="/settings/integrations/procore"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin']}>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.ProcoreIntegrationPage />
          </Suspense>
        </RequireAuth>
      }
    />,
  ];
}

// ============================================================================
// PORTAL ROUTES - REMOVED
// ============================================================================
// Underwriter and Broker portals have been deleted.
// Insurance / Risk Terminal UI archived (RISK-INSURANCE-ARCHIVE); broker/underwriter flows use Command Center.
function getPortalRoutes() {
  return [];
}

// ============================================================================
// SC SPECIFIC ROUTES
// ============================================================================

/**
 * getSCRoutes - Subcontractor-specific pages
 */
function getSCRoutes() {
  return [
    <Route
      key="compliance"
      path="/compliance"
      element={
        <RequireAuth role="scAdmin">
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.Compliance />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="safety-quiz"
      path="/safety-quiz"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.SafetyQuiz />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="equipment"
      path="/dashboard/equipment"
      element={
        <RequireAuth role={['foreman', 'scAdmin', 'gcAdmin', 'admin']}>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.Equipment />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="worker-hours"
      path="/dashboard/worker-hours"
      element={
        <RequireAuth role={['scAdmin', 'gcAdmin', 'admin']}>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.WorkerHours />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="osha-300"
      path="/dashboard/osha-300"
      element={
        <RequireAuth role={['scAdmin', 'gcAdmin', 'admin']}>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.OSHACompliance />
          </Suspense>
        </RequireAuth>
      }
    />,
    // Incident Reports Route - Using DarkIncidents for unified dark mode UI
    <Route
      key="incidents"
      path="/incidents"
      element={
        <RequireAuth role={['scAdmin', 'gcAdmin', 'admin']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkIncidents />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    // Training Management Hub - Using DarkTraining for unified dark mode UI
    <Route
      key="training"
      path="/training"
      element={
        <RequireAuth role={['scAdmin', 'gcAdmin', 'admin']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkTraining />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    // Learning-platform content admin console — roles mirror the server's
    // requireAdmin (admin|gcAdmin|scAdmin) on /api/learning/admin/*.
    // Platform-global curation surface, so no SubscriptionGate.
    <Route
      key="learning-admin"
      path="/learning-admin"
      element={
        <RequireAuth role={['admin', 'gcAdmin', 'scAdmin']}>
          <ErrorBoundary>
            <Suspense fallback={<DarkSuspenseFallback />}>
              <LazyComponents.DarkLearningAdmin />
            </Suspense>
          </ErrorBoundary>
        </RequireAuth>
      }
    />,
    <Route
      key="sc-reports"
      path="/sc/reports"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.SCReports />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="gc-reports"
      path="/gc/reports"
      element={
        <RequireAuth>
          <Suspense fallback={<DarkSuspenseFallback />}>
            <LazyComponents.GCReports />
          </Suspense>
        </RequireAuth>
      }
    />,
    <Route
      key="analytics"
      path="/analytics"
      element={
        <RequireAuth>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkAnalytics />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
  ];
}

// ============================================================================
// COMMON ROUTES
// ============================================================================

/**
 * getCommonRoutes - Common pages used across all roles
 */
function getCommonRoutes() {
  return [
    <Route
      key="projects"
      path="/projects"
      element={
        <RequireAuth>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkProjects />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    <Route
      key="schedule"
      path="/schedule"
      element={
        <RequireAuth>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkSchedule />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    <Route
      key="workers"
      path="/workers"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkWorkers />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    <Route
      key="foreman"
      path="/foreman"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin', 'foreman']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkForemanDashboard />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    <Route
      key="planning-session"
      path="/foreman/planning/:sessionId"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin', 'foreman']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.PlanningSessionPage />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    <Route
      key="assets"
      path="/assets"
      element={
        <RequireAuth role={['gcAdmin', 'scAdmin', 'admin']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkAssetRegistry />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    // Debug/Diagnostics route - Admin-only, for production troubleshooting
    // @VH_DELETE_CANDIDATE: Gate with admin role check or env flag | delete_after=2026-02-15
    <Route
      key="debug"
      path="/__debug"
      element={
        <RequireAuth role={['admin', 'gcAdmin']}>
          <ErrorBoundary>
            <DebugPage />
          </ErrorBoundary>
        </RequireAuth>
      }
    />,
  ];
}

// ============================================================================
// ML/GLASS DASHBOARD ROUTES - REMOVED
// ============================================================================
// ML Dashboard and Glass Dashboard have been deleted
function getMLRoutes() {
  return [];
}

// ============================================================================
// PLACEHOLDER ROUTES
// ============================================================================

/**
 * getPlaceholderRoutes - Placeholder routes for remaining navigation items
 */
function getPlaceholderRoutes() {
  return [
    <Route
      key="admin-users"
      path="/admin/users"
      element={
        <RequireAuth role={['admin', 'gcAdmin', 'scAdmin']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkAdminUsers />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
    <Route
      key="admin-roles"
      path="/admin/roles"
      element={
        <RequireAuth role={['admin', 'gcAdmin', 'scAdmin']}>
          <RedirectWithQuery to="/admin/users" defaults={{ tab: 'roles' }} />
        </RequireAuth>
      }
    />,
    <Route
      key="admin-activity"
      path="/admin/activity"
      element={
        <RequireAuth role={['admin', 'gcAdmin', 'scAdmin']}>
            <ErrorBoundary>
              <Suspense fallback={<DarkSuspenseFallback />}>
                <LazyComponents.DarkActivityLog />
              </Suspense>
            </ErrorBoundary>
        </RequireAuth>
      }
    />,
  ];
}

// ============================================================================
// LEGAL/SUPPORT ROUTES
// ============================================================================

/**
 * getLegalRoutes - Privacy policy and terms of service
 */
function getLegalRoutes() {
  return [
    <Route key="privacy" path="/privacy" element={<PrivacyPolicy />} />,
    <Route key="terms" path="/terms" element={<TermsOfService />} />,
  ];
}

// ============================================================================
// MARKETING ROUTES
// ============================================================================

/**
 * getMarketingRoutes - Features and pricing pages
 */
function getMarketingRoutes() {
  return [
    <Route key="features" path="/features" element={<Features />} />,
    <Route key="pricing" path="/pricing" element={<Pricing />} />,
    <Route key="solutions" path="/solutions" element={<SolutionsPage />} />,
  ];
}

// ============================================================================
// DEFAULT ROUTES
// ============================================================================

/**
 * getDefaultRoutes - Dashboard selector and domain-aware home page
 * 
 * Home Route Behavior:
 * - virtual-hardhat.com / www.virtual-hardhat.com: Shows marketing LandingPage
 * - app.virtual-hardhat.com: Redirects to /command-center (if authenticated) or /login
 * 
 * This enables the same vh-web app to serve both the marketing site and dashboard.
 */
function getDefaultRoutes() {
  return [
    <Route key="selector" path="/selector" element={<DashboardSelector />} />,
    // Domain-aware home: marketing site on virtual-hardhat.com, dashboard redirect on app.virtual-hardhat.com
    <Route key="home" path="/" element={<DomainAwareHome />} />,
  ];
}

/**
 * get404Route - 404 catch-all route (MUST be added LAST)
 */
export function get404Route() {
  return <Route key="not-found" path="*" element={<NotFound />} />;
}
